import { useAnalysisStore } from '@modules/analysis/store/analysisStore';
import {
  DEFAULT_COMPARE_DURATION_A_NAME,
  DEFAULT_COMPARE_DURATION_B_NAME,
  DEFAULT_COMPARE_FILTER_A_NAME,
  DEFAULT_COMPARE_FILTER_B_NAME,
  DEFAULT_COMPARE_SORT_BY,
  DEFAULT_COMPARE_TO_WHERECLAUSE_FILTER,
} from '@modules/quantify/constants';
import {
  useFetchQuantifyReport,
  useIsDurationCustomizationPossible,
} from '@modules/quantify/hooks';
import { QuantifyActionType } from '@modules/quantify/store/quantifyActionType';
import { useQuantifyStore } from '@modules/quantify/store/quantifyStore';
import {
  getCompareToPreviousPeriodFromDuration,
  getDefaultCompareToCustomAbsoluteDuration,
  isValidQuery,
  trackQuantifyCompareEvents,
} from '@modules/quantify/utils';
import { AccessTimeRounded, FilterAltRounded } from '@mui/icons-material';
import {
  Box,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  MenuList,
  Tooltip,
} from '@mui/material';
import { QuantifyVisualisationType } from 'generated/graphql';
import { useEffect, useState } from 'react';
import { useGetUser } from 'v2/core/services/user';

interface CompareActionMenuProps {
  anchorEl: HTMLElement | null;
  closeMenu: () => void;
}

export const CompareActionMenu = ({
  anchorEl,
  closeMenu,
}: CompareActionMenuProps) => {
  const {
    tenant: { timezone },
  } = useGetUser();
  const isDurationCustomizationPossible = useIsDurationCustomizationPossible();
  const dispatch = useQuantifyStore(({ dispatch }) => dispatch);
  const compareTo = useQuantifyStore(({ query }) => query.compareQuery);
  const duration = useQuantifyStore(({ query }) => query.duration);
  const isValidQueryState = useQuantifyStore(({ query }) =>
    isValidQuery(query),
  );
  const shouldHighlightDurationField = useQuantifyStore(
    ({ visual }) => visual.shouldHighlightDurationField,
  );
  const shouldHighlightWhereClauseField = useQuantifyStore(
    ({ visual }) => visual.shouldHighlightWhereClauseField,
  );

  const fetchReportData = useFetchQuantifyReport();
  const [shouldFetchReportData, setShouldFetchReportData] = useState(false);

  const analysisLink = useAnalysisStore(({ href }) => href);

  useEffect(() => {
    if (shouldFetchReportData && isValidQueryState) {
      setShouldFetchReportData(false);
      fetchReportData();
    }
  }, [fetchReportData, isValidQueryState, shouldFetchReportData]);

  const isCompareByTimeSelected =
    !!compareTo?.previousPeriod || !!compareTo?.timerange;
  const isCompareByFilterSelected = !!compareTo?.whereClause;

  const onCompareTypeChange = (
    baseQueryName: string,
    deltaQueryName: string,
  ) => {
    dispatch({
      type: QuantifyActionType.SET_QUERY,
      payload: {
        sortBy: DEFAULT_COMPARE_SORT_BY,
        shouldShowTopDataPoints: true,
        visibleDataPoints: [],
        visualisationType: QuantifyVisualisationType.Bar,
        compareMetadata: {
          baseQueryName,
          deltaQueryName,
        },
      },
    });

    dispatch({
      type: QuantifyActionType.SET_REPORT_DATA,
      payload: {
        compare: {
          tableRows: null,
          chartDataPoints: null,
        },
        quantify: {
          tableRows: null,
          chartDataPoints: null,
        },
      },
    });
  };

  const selectCompareByTime = () => {
    if (isCompareByTimeSelected) {
      closeMenu();
      return;
    }
    dispatch({
      type: QuantifyActionType.SET_QUERY,
      payload: {
        compareQuery:
          duration.type === 'last'
            ? getCompareToPreviousPeriodFromDuration(duration)
            : getDefaultCompareToCustomAbsoluteDuration(timezone),
      },
    });
    onCompareTypeChange(
      DEFAULT_COMPARE_DURATION_A_NAME,
      DEFAULT_COMPARE_DURATION_B_NAME,
    );
    setShouldFetchReportData(true);
    closeMenu();
    trackQuantifyCompareEvents({
      eventName: 'Quantify Compare - By Time Option Selected',
      link: analysisLink,
    });
  };

  const selectCompareByFilter = () => {
    if (isCompareByFilterSelected) {
      closeMenu();
      return;
    }
    onCompareTypeChange(
      DEFAULT_COMPARE_FILTER_A_NAME,
      DEFAULT_COMPARE_FILTER_B_NAME,
    );
    dispatch({
      type: QuantifyActionType.SET_QUERY,
      payload: {
        compareQuery: DEFAULT_COMPARE_TO_WHERECLAUSE_FILTER,
      },
    });
    closeMenu();
    trackQuantifyCompareEvents({
      eventName: 'Quantify Compare - By Another Filter Option Selected',
      link: analysisLink,
    });
  };

  const isOpen = Boolean(anchorEl);

  // Make sure to reset highlight state when menu is closed
  useEffect(() => {
    if (isOpen) {
      return;
    }

    if (shouldHighlightDurationField) {
      dispatch({
        type: QuantifyActionType.SET_VISUAL,
        payload: {
          shouldHighlightDurationField: false,
        },
      });
    }

    if (shouldHighlightWhereClauseField) {
      dispatch({
        type: QuantifyActionType.SET_VISUAL,
        payload: {
          shouldHighlightWhereClauseField: false,
        },
      });
    }
  }, [
    dispatch,
    isOpen,
    shouldHighlightDurationField,
    shouldHighlightWhereClauseField,
  ]);

  return (
    <Menu
      id="compare-select-menu"
      anchorEl={anchorEl}
      open={isOpen}
      onClose={closeMenu}
    >
      <MenuList disablePadding>
        <MenuItem
          selected={isCompareByFilterSelected}
          onClick={selectCompareByFilter}
          onMouseEnter={() => {
            dispatch({
              type: QuantifyActionType.SET_VISUAL,
              payload: {
                shouldHighlightWhereClauseField: true,
              },
            });
          }}
          onMouseLeave={() => {
            dispatch({
              type: QuantifyActionType.SET_VISUAL,
              payload: {
                shouldHighlightWhereClauseField: false,
              },
            });
          }}
        >
          <ListItemIcon>
            <FilterAltRounded
              sx={{
                color: 'text.secondary',
              }}
            />
          </ListItemIcon>
          <ListItemText>By Another Filter</ListItemText>
        </MenuItem>
        <Tooltip
          title={
            isDurationCustomizationPossible
              ? undefined
              : 'Compare By Time is not applicable to queries which do no contain any Feedback Fields'
          }
          placement="top"
        >
          <Box>
            <MenuItem
              disabled={!isDurationCustomizationPossible}
              selected={isCompareByTimeSelected}
              onClick={selectCompareByTime}
              onMouseEnter={() => {
                dispatch({
                  type: QuantifyActionType.SET_VISUAL,
                  payload: {
                    shouldHighlightDurationField: true,
                  },
                });
              }}
              onMouseLeave={() => {
                dispatch({
                  type: QuantifyActionType.SET_VISUAL,
                  payload: {
                    shouldHighlightDurationField: false,
                  },
                });
              }}
            >
              <ListItemIcon>
                <AccessTimeRounded
                  sx={{
                    color: 'text.secondary',
                  }}
                />
              </ListItemIcon>
              <ListItemText>By Time</ListItemText>
            </MenuItem>
          </Box>
        </Tooltip>
      </MenuList>
    </Menu>
  );
};
